import {useCallback, useEffect, useState} from "react";
import {useNavigate} from "react-router";

interface UnsavedChangesMessages {
    title?: string;
    description?: string;
    confirm?: string;
}

export function useUnsavedChangesGuard(dirty: boolean, messages: UnsavedChangesMessages = {}) {
    const navigate = useNavigate();
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

    const run = useCallback((action: () => void) => {
        if (!dirty) {
            action();
            return;
        }
        setPendingAction(() => action);
    }, [dirty]);

    useEffect(() => {
        if (!dirty) return;
        const warnBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
        window.addEventListener("beforeunload", warnBeforeUnload);
        return () => window.removeEventListener("beforeunload", warnBeforeUnload);
    }, [dirty]);

    useEffect(() => {
        if (!dirty) return;
        const guardLinkNavigation = (event: MouseEvent) => {
            if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
            const target = event.target;
            if (!(target instanceof Element)) return;
            const link = target.closest<HTMLAnchorElement>("a[href]");
            if (!link || link.target === "_blank" || link.hasAttribute("download") || link.href === window.location.href) return;

            event.preventDefault();
            event.stopPropagation();
            const targetUrl = new URL(link.href);
            run(() => {
                if (targetUrl.origin === window.location.origin) {
                    navigate({pathname: targetUrl.pathname, search: targetUrl.search, hash: targetUrl.hash});
                } else {
                    window.location.assign(targetUrl.href);
                }
            });
        };
        document.addEventListener("click", guardLinkNavigation, true);
        return () => document.removeEventListener("click", guardLinkNavigation, true);
    }, [dirty, navigate, run]);

    const confirm = useCallback(() => {
        const action = pendingAction;
        setPendingAction(null);
        action?.();
    }, [pendingAction]);

    return {
        run,
        dialogProps: {
            open: pendingAction !== null,
            onOpenChange: (open: boolean) => {
                if (!open) setPendingAction(null);
            },
            title: messages.title ?? "Ungespeicherte Änderungen verwerfen?",
            description: messages.description ?? "Die bereits vorgenommenen Änderungen gehen verloren.",
            confirm: messages.confirm ?? "Änderungen verwerfen",
            onConfirm: confirm,
        },
    };
}
